import React from "react";
import {
    Button,
    Badge,
    Card,
    Col,
    notification,
    message,
    PageHeader,
    Row,
    Upload,
    Dropdown,
    Divider,
    Result,
    Alert,
    Modal,
} from "antd";
import {UploadOutlined, DownOutlined, DownloadOutlined, FileSyncOutlined, WarningOutlined} from '@ant-design/icons';
import {API} from '../api';
import {handleError, logGAEvent, checkPermission } from "../utils/util";
import JSONInput from 'react-json-editor-ajrm';
import locale from 'react-json-editor-ajrm/locale/en';
import CsvDownload from 'react-json-to-csv';
import {DropdownMenu} from "./editableTable/dropdownMenu";
import EditableTable from "./extractGuide/editableTable";
import ExtractDiffModal from "./extractGuide/extractDiffModal";
import { DimaContext } from "../DimaContext";
import ExtractDiffTable from "./extractGuide/extractDiffTable";

const { confirm } = Modal;

message.config({
    duration: 3,
    maxCount: 3,
    rtl: false
});

class ValidateFile extends React.Component {
    constructor(props) {
        super(props);
        this.newResourceNameRef = React.createRef();
        this.newVersionNameRef = React.createRef();
        this.pushCommentsRef = React.createRef();
        this.state = {
            resourceType: "",
            fhirErrors: {},
            fileList: [],
            uploading: false,
            resourceTypeOptions: [],
            resourceTypeDefinition: [],
            loading: true,
            showNewResourceTypeModal: false,
            newResourceTypeName: null,
            enableEdits: false,
            duplicateType: null,
            columns: [
                {
                    title: "Column Name",
                    dataIndex: "column_name",
                    key: "column_name",
                    defaultSortOrder: "ascend",
                    sorter: (a, b) => {
                        if (!!a.column_name && !!b.column_name) {
                            return a.column_name.localeCompare(b.column_name)
                        }
                    }
                },
                {
                    title: "Required",
                    dataIndex: "required",
                    key: "required",
                    render: (_) => _.toString().toLowerCase(),
                    sorter: (a, b) => a.required - b.required //true - false === 1, false - true === -1
                },
                {
                    title: "Nullable",
                    dataIndex: "nullable",
                    key: "nullable",
                    render: (_) => {
                        if (_ != null) {
                            return _.toString().toLowerCase()
                        }
                    },
                    sorter: (a, b) => a.nullable - b.nullable
                },
                {
                    title: "Allowed Values",
                    dataIndex: "allowed",
                    key: "allowed",
                    width: 400,
                },
                {
                    title: "Description",
                    dataIndex: "description",
                    key: "description",
                    width: 600,
                },
                {
                    title: "Type",
                    dataIndex: "type",
                    key: "type"
                }
            ]
        }
    }

    componentDidMount = () => {
        document.title = 'File Extract Guides';
        this.getSchemaData();
    }

    componentDidUpdate = () => {
        if (this.context.userData.loaded && typeof(this.state.canEditExtract) === 'undefined') {
            this.setState({canEditExtract: checkPermission(this.context.userData.permissions, 'editExtractGuide')}, () => this.getSchemaData());
        }
    }

    getSchemaData = () => {
        this.setState({loading: true}, async () => {
            await this.getResourceTypeNames();
            this.getResourceTypeDefinition();
            this.setState({
                loading: false,
                newResourceTypeName: null,
            });
        });
    };

    getResourceTypeNames = async () => {
        const resourceTypeNames = await API.get("/meta/extracts/distinct")
        const currentType = this.state.resourceType || '';
        this.setState({
            resourceTypeOptions: resourceTypeNames.data,
            resourceType: (resourceTypeNames.data.length > 0 && this.state.resourceType.length <= 0) ? resourceTypeNames.data[0] : currentType,
        }, async () => {
            await this.getResourceTypeVersions();
        })
    }

    getResourceTypeDefinition = () => {
        if (this.state.resourceType && this.state.resourceDefinitionVersion) {
            this.setState({loading: true}, async () => {
                const resourceTypeDefinition = await API.get(`/validate/extracts/definition?resourceType=${this.state.resourceType}&version=${this.state.resourceDefinitionVersion}`)
                this.setState({
                    resourceTypeDefinition: resourceTypeDefinition.data,
                    loading: false
                });
            });
        }
    }

    getResourceTypeVersions = async () => {
        const versions = this.state.canEditExtract ? await API.get(`/extracts/versions/all?resourceType=${this.state.resourceType}`) : await API.get(`/validate/extracts/versions?resourceType=${this.state.resourceType}`);
        let latest = 1;
        for (const version of versions.data) {
            const match = version.match(/^v(\d+)$/);
            if (match && parseInt(match[1], 10) > latest) {
                latest = parseInt(match[1], 10);
            }
        }
        latest = 'v' + latest.toString();
        if (versions.data.includes(latest)) {
            this.setState({resourceDefinitionVersionOptions: versions.data, resourceDefinitionVersion: latest, latestVersion: latest}, () => this.getResourceTypeDefinition());
        } else {
            this.setState({resourceDefinitionVersionOptions: versions.data, resourceDefinitionVersion: versions.data[0], latestVersion: null}, () => this.getResourceTypeDefinition());
        }
    };

    handleCreateNewResourceType = () => {
        if (this.state.enableEdits) {
            confirm({
                title: 'Are you sure?',
                icon: <WarningOutlined />,
                content: 'Unsaved changes will be lost.',
                okText: 'Ok',
                okType: 'primary',
                cancelText: 'Continue editing',
                onOk: () => {
                    this.setState({showNewResourceTypeModal: true});
                }
            });
        } else {
            this.setState({showNewResourceTypeModal: true});
        }
    }

    handleCreateNewVersion = () => {
        if (this.state.enableEdits) {
            confirm({
                title: 'Are you sure?',
                icon: <WarningOutlined />,
                content: 'Unsaved changes will be lost.',
                okText: 'Ok',
                okType: 'primary',
                cancelText: 'Continue editing',
                onOk: () => {
                    this.setState({ showNewVersionModal: true })
                }
            });
        } else {
            this.setState({ showNewVersionModal: true })
        }
    }

    createNewResourceType = (newType) => {
        if (this.state.resourceTypeOptions.indexOf(newType) > -1) {
            this.setState({duplicateType: 'A resource type with this name already exists.'});
            return;
        } else {
            this.setState({duplicateType: null});
        }
        this.setState(
            {
                resourceTypeOptions: [...this.state.resourceTypeOptions, newType],
                resourceType: newType,
                resourceDefinitionVersion: 'draft',
                resourceDefinitionVersionOptions: ['draft'],
                newResourceTypeName: newType,
            }, () => {
                this.getResourceTypeDefinition();
                this.setState({showNewResourceTypeModal: false});
                this.setEnableEdits(true);
                this.newResourceNameRef.current.value = '';
            }
        );
    };

    createNewVersion = async (newVersion) => {
        if (newVersion.match(/^v\d+$/)) {
            this.setState({newVersionError: 'This is a reserved name.'});
            return;
        } else if (this.state.resourceDefinitionVersionOptions.includes(newVersion)) {
            this.setState({newVersionError: 'Version name already exists.'});
            return;
        } else {
            this.setState({newVersionError: null});
        }

        // copy latest definitions
        const latestDefinitions = await API.get(`/validate/extracts/definition?resourceType=${this.state.resourceType}&version=${this.state.latestVersion}`);
        const newVersionDefinitions = latestDefinitions.data.map(definition => {
            return { ...definition, version: newVersion };
        });

        this.setState(
            {
                resourceDefinitionVersionOptions: [...this.state.resourceDefinitionVersionOptions, newVersion],
                resourceDefinitionVersion: newVersion,
                showNewVersionModal: false,
                resourceTypeDefinition: newVersionDefinitions,
            }, () => {
                this.newVersionNameRef.current.value = '';
                this.setEnableEdits(true);
            }
        );
    };

    pushVersion = async (comments) => {
        if (!comments) {
            return alert('Description of changes is required.');
        }
        try {
            const response = await API.post('/extracts/definition/versionpush', {
                comments,
                resourceType: this.state.resourceType,
                version: this.state.resourceDefinitionVersion,
            });
            if (response.status === 201) {
                notification.success({
                    message: 'Success',
                    description: `Pushed version to official version list`,
                    duration: 2,
                });
                this.getSchemaData();
            }
        } catch (err) {
            handleError(err);
        }
        this.setState({ showPushVersionModal: false });
        this.pushCommentsRef.current.value = '';
    }

    cancelCreateNewResourceType = () => {
        this.newResourceNameRef.current.value = '';
        this.setState({showNewResourceTypeModal: false, duplicateType: null});
    };

    cancelCreateNewVersion = () => {
        this.newVersionNameRef.current.value = '';
        this.setState({showNewVersionModal: false, newVersionError: null});
    }

    cancelPushVersion = () => {
        this.pushCommentsRef.current.value = '';
        this.setState({showPushVersionModal: false});
    }

    setEnableEdits = (val) => {
        this.setState({enableEdits: val});
    };

    changeVersion = (selectedVersion) => {
        this.setState({resourceDefinitionVersion: selectedVersion}, () => {
            this.setEnableEdits(false);
            this.getResourceTypeDefinition();
        });
    }

    handleVersionSelect = (selectedVersion) => {
        if (this.state.enableEdits) {
            confirm({
                title: 'Are you sure?',
                icon: <WarningOutlined />,
                content: 'Unsaved changes will be lost.',
                okText: 'Ok',
                okType: 'primary',
                cancelText: 'Continue editing',
                okButtonProps: { 'data-cy': 'cancel-extract-definition-btn' },
                onOk: () => {
                    this.changeVersion(selectedVersion);
                }
            });
        } else {
            this.changeVersion(selectedVersion);
        }
    }

    changeResourceType = (selectedResourceType) => {
        this.setState({resourceType: selectedResourceType}, async () => {
            await this.getResourceTypeVersions();
            this.setEnableEdits(false);
        });
    }

    handleDropdownSelect = (selectedResourceType) => {
        if (this.state.enableEdits) {
            confirm({
                title: 'Are you sure?',
                icon: <WarningOutlined />,
                content: 'Unsaved changes will be lost.',
                okText: 'Ok',
                okType: 'primary',
                cancelText: 'Continue editing',
                okButtonProps: { 'data-cy': 'cancel-extract-definition-btn' },
                onOk: () => {
                    this.changeResourceType(selectedResourceType);
                }
            });
        } else {
            this.changeResourceType(selectedResourceType);
        }
    };

    handleValidate = () => {
        const {fileList} = this.state;
        const formData = new FormData();
        logGAEvent('Validate (File Extract Guides)');
        fileList.forEach(file => {
            formData.append('file', file);
        });

        this.setState({
            uploading: false,
            fileList: fileList
        }, () => {
            API.post(`/validate/file?resourceType=${this.state.resourceType}&version=${this.state.resourceDefinitionVersion}`, formData).then((res) => {
                this.setState({fhirErrors: res.data})
            }).catch(err => handleError(err))
        });
    };

    getValidateResult = () => {
        if (Object.keys(this.state.fhirErrors).length > 1 && this.state.fileList.length > 0) {
            return <Card>
                <Result
                    status="warning"
                    title={`Errors detected in ${this.state.resourceType} extract.`}
                    subTitle="Please ensure that the file extract contains all columns listed in the table below"
                />
                <JSONInput
                    id={Math.random()}
                    placeholder={this.state.fhirErrors}
                    confirmGood={false}
                    theme={"light_mitsuketa_tribute"}
                    colors={{default: "darkgray", background: "None"}}
                    locale={locale}
                    height='100%'
                />
            </Card>
        } else if (Object.keys(this.state.fhirErrors).length === 1 && this.state.fileList.length > 0) {
            return <Card>
                <Result
                    status="success"
                    title="Success"
                    subTitle={`File extract fully conforms to 1UpHealth's ${this.state.resourceType} extract guidelines and is ready
                    for data ingestion processing. For more information on next steps, please reach out to your Solution Architect
                    or Customer Success Manager.`}
                />
            </Card>
        }

    }
    render() {
        const {uploading, fileList} = this.state;
        const props = {
            maxCount: 1,
            onRemove: file => {
                this.setState(state => {
                    const index = state.fileList.indexOf(file);
                    const newFileList = state.fileList.slice();
                    newFileList.splice(index, 1);
                    return {
                        fileList: newFileList,
                        fhirErrors: {}
                    };
                });
            },
            beforeUpload: file => {
                if (file.type === 'text/csv' || file.type === 'application/json' || file.name.endsWith('.ndjson')) {
                    this.setState(state => ({
                        fileList: [file],
                        fhirErrors: {}
                    }));
                    return false;
                } else {
                    message.error('Only csv, json, ndjson files allowed');
                    return Upload.LIST_IGNORE;
                }
            },
            onChange: info => {
                switch (info.file.status) {
                    case "done":
                        this.setState({uploading: false})
                        break;
                    default:
                        break;
                }
            },
            fileList,
        };

        return (
            <>
                <PageHeader
                    className="site-page-header"
                    onBack={() => window.history.back()}
                    title="Validate File"
                />
                {
                    this.state.resourceDefinitionVersionOptions &&
                    <ExtractDiffModal
                        closeModal={() => this.setState({showDiffModal: false})}
                        isOpen={this.state.showDiffModal}
                        currentVersion={this.state.resourceDefinitionVersion}
                        versionOptions={this.state.resourceDefinitionVersionOptions}
                        resourceType={this.state.resourceType}
                    />
                }
                <Row gutter={[24, 16]}>
                    <Col span={12}>
                        <Card title="FAQ">
                            <p>
                                This page contains the specifications needed to build data extract files processable by
                                1UpHealth.
                                Select the resource type drop down menu to review each template.
                                The files must include the exact column names and allowed values, if applicable in each
                                file.
                            </p>
                            <p>
                                <Alert type="warning" message={<>For more information on how to use this page, please see
                                <a rel="noreferrer" target="_blank" href="https://docs.oneupdocs.com/admin/data-ingestion/flat-file-extraction-guide"> DIMA Flat File Extraction Guide</a>.
                                </>} />
                            </p>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Badge.Ribbon text={<FileSyncOutlined/>} color="volcano">
                            <Card title="Extract Validator">
                                <p>
                                    Before sending data to 1UpHealth for data ingestion, please ensure that
                                    the file extract schema conforms to the guidelines listed in the table below. Files which are
                                    conformant can be processed much more quickly then custom extracts.
                                </p>
                                <p>
                                    To check for conformance, upload a file below to 1Up's extract validator.
                                </p>
                                <p>
                                    Requirements:
                                    <ul>
                                        <li>
                                            File type must be:
                                            <ul>
                                                <li>CSV (comma or pipe separated with backslash (\) used to escape special characters)</li>
                                                <li>JSON (array of JSON objects)</li>
                                                <li>NDJSON (newline delimited JSON)</li>
                                            </ul>
                                        </li>
                                        <li>Files must be smaller than 5MB.</li>
                                    </ul>
                                </p>
                                <p>
                                    For more details regarding data formats and expectations,
                                    see <a rel="noreferrer" target="_blank"
                                                  href="https://docs.oneupdocs.com/admin/data-ingestion/data-ingestion-guide">
                                    1Up Data Ingestion Guide
                                </a>.
                                </p>

                                <Upload {...props}>
                                    <Button icon={<UploadOutlined/>}>Select File</Button>
                                </Upload>

                                <Button
                                    type="primary"
                                    onClick={this.handleValidate}
                                    disabled={fileList.length === 0 || this.state.resourceType.length === 0}
                                    loading={uploading}
                                    style={{marginTop: 16, marginBottom: 15}}
                                >
                                    Validate ({this.state.resourceType})
                                </Button>
                                {this.getValidateResult()}


                            </Card>
                        </Badge.Ribbon>
                    </Col>
                </Row>
                <Divider/>
                <Row>
                    <Col>
                        <Dropdown
                            trigger={['click']}
                            overlay={<DropdownMenu options={this.state.resourceTypeOptions}
                                                   handleMenuClick={(e) => this.handleDropdownSelect(e.key)}/>}>
                            <Button data-cy='select-resource-type-extract-guide'>
                                <b>Resource Type: </b>{this.state.resourceType} <DownOutlined/>
                            </Button>
                        </Dropdown>
                    </Col>
                    {
                        this.state.canEditExtract &&
                        <Col>
                            <Modal
                                title="Enter New Resource Type"
                                open={this.state.showNewResourceTypeModal}
                                onOk={() => this.createNewResourceType(this.newResourceNameRef.current.value)}
                                onCancel={() => this.cancelCreateNewResourceType()}
                            >
                                <input
                                    ref={this.newResourceNameRef}
                                    style={{width: '100%', borderColor: 'rgb(217,217,217)', borderStyle: 'solid', borderWidth: '1px'}}
                                    data-cy="new-resource-type-extract-guide-input"
                                />
                                {this.state.duplicateType && <Result status="error" title="Invalid Resource Type" subTitle={this.state.duplicateType} />}
                            </Modal>
                            <Button style={{marginLeft: '10px',}} onClick={() => this.handleCreateNewResourceType()} data-cy='new-resource-type-extract-guide-button'>New Resource Type</Button>
                        </Col>
                    }
                    <Col style={{alignSelf: 'flex-end', marginLeft: 'auto'}}>
                    {
                        this.state.canEditExtract &&
                        this.state.resourceDefinitionVersion &&
                        !this.state.resourceDefinitionVersion.match(/^v\d+$/) &&
                        !this.state.enableEdits &&
                        <>
                            <Modal
                                title="Summary of Changes"
                                open={this.state.showPushVersionModal}
                                onOk={() => this.pushVersion(this.pushCommentsRef.current.value)}
                                onCancel={() => this.cancelPushVersion()}
                                width='92%'
                            >
                                <div style={{marginTop: '-10px',}}>Describe the changes made in this version:</div><br/>
                                 <textarea
                                    ref={this.pushCommentsRef}
                                    style={{width: '100%', borderColor: 'rgb(217,217,217)', borderStyle: 'solid', borderWidth: '1px'}}
                                    data-cy="push-version-extract-guide-textarea"
                                    rows='5'
                                />
                                <ExtractDiffTable
                                    baseVersion={this.state.latestVersion}
                                    compareVersion={this.state.resourceDefinitionVersion}
                                    resourceType={this.state.resourceType}
                                />
                            </Modal>
                            <Button
                                onClick={() => this.setState({showPushVersionModal: true})}
                            >
                                Push to latest
                            </Button>
                        </>
                    }
                    </Col>
                    <Col style={{alignSelf: 'flex-end', marginLeft: '10px'}}>
                    {
                        this.state.canEditExtract &&
                        <>
                            <Modal
                                title="Enter Version Name"
                                open={this.state.showNewVersionModal}
                                onOk={() => this.createNewVersion(this.newVersionNameRef.current.value)}
                                onCancel={() => this.cancelCreateNewVersion()}
                            >
                                <input
                                    ref={this.newVersionNameRef}
                                    style={{width: '100%', borderColor: 'rgb(217,217,217)', borderStyle: 'solid', borderWidth: '1px'}}
                                    data-cy="new-version-extract-guide-input"
                                />
                                {this.state.newVersionError && <Result status="error" title="Invalid Version Name" subTitle={this.state.newVersionError} />}
                            </Modal>
                            <Button
                                onClick={() => this.handleCreateNewVersion()}
                            >
                                Create New Version
                            </Button>
                        </>
                    }
                    </Col>
                    <Col style={{alignSelf: 'flex-end', marginLeft: '10px'}}>
                    {
                        <Button
                            onClick={() => this.setState({showDiffModal: true})}
                            data-cy='extract-guide-compare-version-button'
                        >
                            Compare Versions
                        </Button>
                    }
                    </Col>
                    <Col style={{alignSelf: 'flex-end', marginLeft: '10px', marginRight: '3px',}}>
                        <Dropdown
                            trigger={['click']}
                            overlay={
                                <DropdownMenu
                                    options={this.state.resourceDefinitionVersionOptions}
                                    handleMenuClick={(e) => this.handleVersionSelect(e.key)}
                                />
                            }
                        >
                            <Button>
                                <b>Version: </b>{this.state.resourceDefinitionVersion}{this.state.resourceDefinitionVersion && this.state.resourceDefinitionVersion === this.state.latestVersion && ' (latest)'}<DownOutlined/>
                            </Button>
                        </Dropdown>
                    </Col>
                    <Col style={{alignSelf: 'flex-end'}}>
                        <CsvDownload
                            filename={(`1up_${this.state.resourceType}_file_extract_definition.csv`).toLowerCase()}
                            style={{
                                border: "none",
                                background: 'white'
                            }}
                            data={this.state.resourceTypeDefinition.map(def => {
                                return {
                                    Column_Name: def.column_name,
                                    Required: def.required,
                                    Nullable: def.nullable,
                                    Allowed_Values: def.allowed,
                                    Description: def.description,
                                    Type: def.type,
                                    Version: def.version,
                                };
                            })}>
                            <Button icon={<DownloadOutlined/>}>Download</Button>
                        </CsvDownload>
                    </Col>
                </Row>
                <br/>
                <EditableTable
                    dataSource={this.state.resourceTypeDefinition}
                    resourceType={this.state.resourceType}
                    setEnableEdits={this.setEnableEdits}
                    enableEdits={this.state.enableEdits}
                    selectedVersion={this.state.resourceDefinitionVersion}
                    loading={this.state.loading}
                    getResourceTypeDefinition={this.getResourceTypeDefinition}
                    newResourceTypeName={this.state.newResourceTypeName}
                />
            </>
        );
    }
}

ValidateFile.contextType = DimaContext;

export default ValidateFile